import { memo } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';
import { TextBlock, CloseIcon } from '@library';
import HorizontalLayout from '../../../../core/components/layout/HorizontalLayout';
import usePriceFormatter from '../../../../core/hooks/usePriceFormatter';
import { removeSearchParams } from '../../../../core/util/removeSearchParam';
import { colors } from '../../ProductFilters/FilterColor';
import type { ProductBestAvailabilityFilter } from '../context/ProductListOptionsContext';
import usePagination from '../../../hooks/usePagination';
import styles from './ProductAppliedFiltersList.module.css';

const ProductAppliedFilterItemContent = memo(
    ({
        children,
        mobile,
        removeFilterItem,
    }: {
        children: ReactNode;
        mobile?: boolean;
        removeFilterItem(): void;
    }) => {
        return (
            <div className={styles.productAppliedFiltersListItem}>
                <div
                    className={clsx(styles.filterItem, {
                        [styles.mobile]: mobile,
                    })}
                >
                    <TextBlock tertiary medium uppercase lineHeightAsFontSize>
                        <HorizontalLayout align="center">
                            <span>{children}</span>
                            <div
                                role="button"
                                tabIndex={0}
                                className={styles.removeFilterButton}
                                onClick={() => {
                                    removeFilterItem();
                                }}
                                onKeyPress={e => {
                                    if (['Enter', ' '].includes(e.key)) {
                                        removeFilterItem();
                                    }
                                }}
                            >
                                <CloseIcon />
                            </div>
                        </HorizontalLayout>
                    </TextBlock>
                </div>
            </div>
        );
    }
);

const ProductAppliedFilterItem = ({
    name,
    value,
    slug,
    label,
    removeFilterItem,
    mobile,
}: {
    name: string;
    value: string;
    slug?: string;
    label?: string;
    removeFilterItem(): void;
    mobile?: boolean;
}) => {
    const isColor = !!colors.get(value)?.name;
    return (
        <ProductAppliedFilterItemContent
            mobile={mobile}
            removeFilterItem={removeFilterItem}
        >
            {name}
            {name ? ': ' : ''}
            {isColor ? (
                <FormattedMessage id={colors.get(value)?.name ?? value} />
            ) : (
                label || slug || value
            )}
        </ProductAppliedFilterItemContent>
    );
};

export const ProductAppliedFilterItemPriceItem = memo(
    ({
        minPrice,
        maxPrice,
        mobile,
    }: {
        minPrice?: number;
        maxPrice?: number;
        mobile?: boolean;
    }) => {
        const { search } = useLocation();
        const { navigateToPage } = usePagination();
        const searchParams = new URLSearchParams(search);

        const priceFormatter = usePriceFormatter();

        const removePriceFilterItem = () => {
            const updatedSearchParams = removeSearchParams(searchParams, [
                'minPrice',
                'maxPrice',
            ]);
            navigateToPage(1, updatedSearchParams.toString());
        };

        const { currency } = priceFormatter(minPrice ?? maxPrice);
        const formattedMinPrice = priceFormatter(minPrice).price;
        const formattedMaxPrice = priceFormatter(maxPrice).price;

        if (minPrice && maxPrice) {
            return (
                <ProductAppliedFilterItemContent
                    mobile={mobile}
                    removeFilterItem={removePriceFilterItem}
                >
                    <FormattedMessage id="price" />
                    {': '}
                    {`${currency} ${formattedMinPrice} - ${currency} ${formattedMaxPrice}`}
                </ProductAppliedFilterItemContent>
            );
        }

        if (minPrice) {
            return (
                <ProductAppliedFilterItemContent
                    mobile={mobile}
                    removeFilterItem={removePriceFilterItem}
                >
                    <FormattedMessage id="minPrice" />
                    {': '}
                    {`${currency} ${formattedMinPrice}`}
                </ProductAppliedFilterItemContent>
            );
        }

        if (maxPrice) {
            return (
                <ProductAppliedFilterItemContent
                    mobile={mobile}
                    removeFilterItem={removePriceFilterItem}
                >
                    <FormattedMessage id="maxPrice" />
                    {': '}
                    {`${currency} ${formattedMaxPrice}`}
                </ProductAppliedFilterItemContent>
            );
        }

        return null;
    }
);

export default memo(ProductAppliedFilterItem);

export const ProductAppliedFilterAvailabilityItem = memo(
    ({
        bestAvailability,
        mobile,
    }: {
        bestAvailability?: ProductBestAvailabilityFilter;
        mobile?: boolean;
    }) => {
        const [searchParams] = useSearchParams();

        const { navigateToPage } = usePagination();

        if (!bestAvailability) {
            return null;
        }

        const removeFilter = () => {
            searchParams.delete('bestAvailability');
            navigateToPage(1, searchParams.toString());
        };

        const getValueIntlId = () => {
            switch (bestAvailability.value) {
                case 1:
                    return 'immediately';
                case 7:
                    return 'within 1 week';
                case 14:
                    return 'within 2 weeks';
                case 28:
                    return 'within 1 month';
                default:
                    return 'ERROR';
            }
        };

        return (
            <ProductAppliedFilterItemContent
                mobile={mobile}
                removeFilterItem={removeFilter}
            >
                <FormattedMessage id="availability" />
                {': '}
                <FormattedMessage id={getValueIntlId()} />
            </ProductAppliedFilterItemContent>
        );
    }
);
